import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Shield,
  Plus,
  AlertTriangle,
  AlertOctagon,
  Activity,
  Calendar,
  TrendingDown,
  Loader2,
  FileText,
  CheckCircle,
  Clock,
  Settings,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ExportButtons } from '../components/ExportButtons';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const INCIDENT_TYPES = [
  { value: 'presqu_accident', label: 'Presqu\'accident', color: 'bg-yellow-500' },
  { value: 'accident_sans_arret', label: 'Accident sans arrêt', color: 'bg-orange-500' },
  { value: 'accident_avec_arret', label: 'Accident avec arrêt', color: 'bg-red-500' },
  { value: 'accident_grave', label: 'Accident grave', color: 'bg-red-700' }
];

const SEVERITY_LEVELS = [
  { value: 'mineur', label: 'Mineur', color: 'bg-blue-500' },
  { value: 'modere', label: 'Modéré', color: 'bg-yellow-500' },
  { value: 'severe', label: 'Sévère', color: 'bg-orange-500' },
  { value: 'critique', label: 'Critique', color: 'bg-red-600' }
];

const STATUS_WORKFLOW = [
  { value: 'ouvert', label: 'Déclaré', color: 'bg-blue-500', next: 'valide' },
  { value: 'valide', label: 'Validé', color: 'bg-purple-500', next: 'cloture' },
  { value: 'cloture', label: 'Clôturé', color: 'bg-green-500', next: null }
];

const Safety = () => {
  const { getAuthHeaders, isManager, isAdmin, user, isDemoReadonly } = useAuth();
  const { t, i18n } = useTranslation();
  
  // Supervisors and above can declare incidents
  const canDeclareIncident = () => user?.role === 'superviseur' || user?.role === 'manager' || user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [config, setConfig] = useState({ hours_worked_annual: 200000 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedYear, setSelectedYear] = useState(isDemoReadonly() ? 2025 : new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    site_id: '',
    zone_id: '',
    incident_date: new Date().toISOString().split('T')[0],
    incident_type: 'presqu_accident',
    severity: 'mineur',
    description: '',
    cause: '',
    days_lost: 0,
    corrective_action: '',
    injured_person: ''
  });
  const [configForm, setConfigForm] = useState({ hours_worked_annual: 200000 });

  useEffect(() => {
    fetchData();
  }, [selectedYear, statusFilter]);

  const fetchData = async () => {
    try {
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const [statsRes, incidentsRes, sitesRes, zonesRes, configRes] = await Promise.all([
        axios.get(`${API}/safety/stats?year=${selectedYear}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/safety/incidents?days=365${statusParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/safety/config`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setIncidents(incidentsRes.data);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      setConfig(configRes.data);
      setConfigForm({ hours_worked_annual: configRes.data.hours_worked_annual });
    } catch (error) {
      console.error('Error fetching safety data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.site_id || !formData.description) {
      toast.error(t('safety.fillRequiredFields'));
      return;
    }
    
    try {
      await axios.post(`${API}/safety/incidents`, {
        ...formData,
        incident_date: new Date(formData.incident_date).toISOString(),
        zone_id: formData.zone_id || null
      }, { headers: getAuthHeaders() });
      toast.success(t('safety.incidentDeclaredSuccess'));
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('safety.declarationError'));
    }
  };

  const handleStatusChange = async (incident, newStatus) => {
    try {
      await axios.patch(`${API}/safety/incidents/${incident.id}/status?status=${newStatus}`, {}, { headers: getAuthHeaders() });
      toast.success(`${t('safety.statusUpdated')}: ${getStatusLabel(newStatus)}`);
      fetchData();
      if (selectedIncident?.id === incident.id) {
        setSelectedIncident({ ...incident, status: newStatus });
      }
    } catch (error) {
      toast.error(t('safety.statusUpdateError'));
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/safety/config?hours_worked_annual=${configForm.hours_worked_annual}`, {}, { headers: getAuthHeaders() });
      toast.success(t('common.configSaved'));
      setConfigDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('safety.saveError'));
    }
  };

  const resetForm = () => {
    setFormData({
      site_id: '',
      zone_id: '',
      incident_date: new Date().toISOString().split('T')[0],
      incident_type: 'presqu_accident',
      severity: 'mineur',
      description: '',
      cause: '',
      days_lost: 0,
      corrective_action: '',
      injured_person: ''
    });
  };

  const openIncidentDetail = (incident) => {
    setSelectedIncident(incident);
    setDetailDialogOpen(true);
  };

  const getIncidentTypeBadge = (type) => {
    const config = INCIDENT_TYPES.find(t => t.value === type);
    return <Badge className={`${config?.color || 'bg-gray-500'} text-white`}>{t(`safety.incidentTypes.${type}`) || type}</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const config = SEVERITY_LEVELS.find(s => s.value === severity);
    return <Badge className={`${config?.color || 'bg-gray-500'} text-white`}>{t(`safety.severities.${severity}`) || severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_WORKFLOW.find(s => s.value === status);
    const statusKey = status === 'ouvert' ? 'declared' : status === 'valide' ? 'validated' : 'closed';
    return (
      <Badge className={`${statusConfig?.color || 'bg-gray-500'} text-white`}>
        {t(`safety.statuses.${statusKey}`) || status}
      </Badge>
    );
  };

  const getStatusLabel = (status) => {
    const statusKey = status === 'ouvert' ? 'declared' : status === 'valide' ? 'validated' : 'closed';
    return t(`safety.statuses.${statusKey}`) || status;
  };

  const getNextStatus = (currentStatus) => {
    const statusConfig = STATUS_WORKFLOW.find(s => s.value === currentStatus);
    return statusConfig?.next;
  };

  const pieColors = ['#3B82F6', '#F59E0B', '#F97316', '#DC2626'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0E346E]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="safety-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            {t('safety.title')}
          </h1>
          <p className="text-slate-500 mt-1">{t('safety.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="ouvert">{t('safety.statuses.declared')}</SelectItem>
              <SelectItem value="valide">{t('safety.statuses.validated')}</SelectItem>
              <SelectItem value="cloture">{t('safety.statuses.closed')}</SelectItem>
            </SelectContent>
          </Select>
          <ExportButtons 
            module="safety" 
            filters={{
              start_date: `${selectedYear}-01-01`,
              end_date: `${selectedYear}-12-31`,
              status: statusFilter !== 'all' ? statusFilter : undefined
            }}
            language={i18n.language || 'fr'}
          />
          {isAdmin() && (
            <Button variant="outline" onClick={() => setConfigDialogOpen(true)} title={t('settings.title')}>
              <Settings className="w-4 h-4" />
            </Button>
          )}
          {canDeclareIncident() && !isDemoReadonly() && (
            <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="declare-incident-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('safety.declareIncident')}
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('safety.totalIncidents')}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_incidents || 0}</p>
              </div>
              <div className="w-12 h-12 bg-slate-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('safety.nearMisses')}</p>
                <p className="text-3xl font-bold text-yellow-600 mt-1">{stats?.near_misses || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('safety.withStop')}</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{stats?.accidents_with_stop || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                <AlertOctagon className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('safety.frequencyRate')}</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.frequency_rate || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Base: {(config?.hours_worked_annual || 200000).toLocaleString()}h</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{t('safety.daysLost')}</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">{stats?.days_lost || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Status Summary */}
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            {t('safety.workflowTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 py-2">
            {STATUS_WORKFLOW.map((status, index) => {
              const count = incidents.filter(i => i.status === status.value).length;
              return (
                <React.Fragment key={status.value}>
                  <div className="flex-1 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${status.color} text-white text-2xl font-bold`}>
                      {count}
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-700">{t(`safety.statuses.${status.value === 'ouvert' ? 'declared' : status.value === 'valide' ? 'validated' : 'closed'}`)}</p>
                  </div>
                  {index < STATUS_WORKFLOW.length - 1 && (
                    <ArrowRight className="w-6 h-6 text-slate-300 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              {t('safety.monthlyTrend')} {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.monthly_trend && stats.monthly_trend.some(m => m.total > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tickFormatter={(v) => ['J','F','M','A','M','J','J','A','S','O','N','D'][v-1]} />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(v) => t(`common.months.${['january','february','march','april','may','june','july','august','september','october','november','december'][v-1]}`)}
                    formatter={(value, name) => [value, name === 'total' ? t('common.total') : name === 'accidents' ? t('safety.accidents') : t('safety.daysLost')]}
                  />
                  <Bar dataKey="total" fill="#64748B" name={t('common.total')} />
                  <Bar dataKey="accidents" fill="#DC2626" name={t('safety.accidents')} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('safety.noIncidents')} {selectedYear}</p>
                  <p className="text-sm text-green-600 font-medium mt-2">{t('safety.excellent')}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* By Severity */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t('safety.bySeverity')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.by_severity && Object.values(stats.by_severity).some(v => v > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: t('safety.severities.mineur'), value: stats.by_severity.mineur || 0 },
                      { name: t('safety.severities.modere'), value: stats.by_severity.modere || 0 },
                      { name: t('safety.severities.severe'), value: stats.by_severity.severe || 0 },
                      { name: t('safety.severities.critique'), value: stats.by_severity.critique || 0 }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieColors.map((color, index) => (
                      <Cell key={index} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p className="text-green-600 font-medium">{t('safety.zeroIncidents')}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Incidents */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">{t('safety.recentIncidents')}</CardTitle>
        </CardHeader>
        <CardContent>
          {incidents.length > 0 ? (
            <div className="space-y-3">
              {incidents.slice(0, 10).map(incident => {
                const site = sites.find(s => s.id === incident.site_id);
                const nextStatus = getNextStatus(incident.status);
                return (
                  <div 
                    key={incident.id} 
                    className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openIncidentDetail(incident)}
                    data-testid={`incident-${incident.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getIncidentTypeBadge(incident.incident_type)}
                          {getSeverityBadge(incident.severity)}
                          {getStatusBadge(incident.status)}
                          {incident.days_lost > 0 && (
                            <Badge className="bg-orange-100 text-orange-800">{incident.days_lost} {t('safety.days')}</Badge>
                          )}
                        </div>
                        <p className="font-medium text-slate-900 line-clamp-1">{incident.description}</p>
                        <p className="text-sm text-slate-500">
                          {site?.name || 'N/A'} • {new Date(incident.incident_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {nextStatus && isManager() && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(incident, nextStatus); }}
                            className="text-xs"
                          >
                            {nextStatus === 'valide' ? t('safety.validate') : t('safety.close')}
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t('safety.noIncidentsDeclared')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Incident Declaration Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl text-red-600">
              {t('safety.incidentDeclaration')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('performance.unit')} *</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v, zone_id: ''})}>
                  <SelectTrigger className="border-2">
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('actions.zone')} ({t('common.optional')})</Label>
                <Select value={formData.zone_id || 'none'} onValueChange={(v) => setFormData({...formData, zone_id: v === 'none' ? '' : v})}>
                  <SelectTrigger className="border-2">
                    <SelectValue placeholder={t('common.select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('common.none')}</SelectItem>
                    {zones.filter(z => z.site_id === formData.site_id).map(zone => (
                      <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('safety.incidentDate')} *</Label>
                <Input
                  type="date"
                  value={formData.incident_date}
                  onChange={(e) => setFormData({...formData, incident_date: e.target.value})}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Type d'incident *</Label>
                <Select value={formData.incident_type} onValueChange={(v) => setFormData({...formData, incident_type: v})}>
                  <SelectTrigger className="border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INCIDENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Gravité</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                  <SelectTrigger className="border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITY_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Jours d'arrêt</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.days_lost}
                  onChange={(e) => setFormData({...formData, days_lost: parseInt(e.target.value) || 0})}
                  className="border-2"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Décrivez l'incident..."
                className="border-2"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Cause</Label>
              <Textarea
                value={formData.cause}
                onChange={(e) => setFormData({...formData, cause: e.target.value})}
                placeholder="Identifiez la ou les causes..."
                className="border-2"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Action corrective</Label>
              <Textarea
                value={formData.corrective_action}
                onChange={(e) => setFormData({...formData, corrective_action: e.target.value})}
                placeholder="Actions mises en place..."
                className="border-2"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Personne blessée (si applicable)</Label>
              <Input
                value={formData.injured_person}
                onChange={(e) => setFormData({...formData, injured_person: e.target.value})}
                placeholder={t('safety.injuredPersonPlaceholder')}
                className="border-2"
              />
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700">
              {t('safety.declareTheIncident')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {t('safety.configTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">{t('safety.referenceHours')}</Label>
              <Input
                type="number"
                min="1000"
                value={configForm.hours_worked_annual}
                onChange={(e) => setConfigForm({...configForm, hours_worked_annual: parseInt(e.target.value) || 200000})}
                className="border-2"
              />
              <p className="text-xs text-slate-500">
                {t('safety.referenceHoursHelp')}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{t('safety.frequencyFormula')}</strong>
              </p>
              <p className="text-sm text-blue-700 mt-2">
                <strong>{t('safety.severityFormula')}</strong>
              </p>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveConfig} className="bg-[#0E346E] hover:bg-[#0A2850]">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              DÉTAIL DE L'INCIDENT
            </DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                {getIncidentTypeBadge(selectedIncident.incident_type)}
                {getSeverityBadge(selectedIncident.severity)}
                {getStatusBadge(selectedIncident.status)}
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500 uppercase">Date</Label>
                  <p className="font-medium">{new Date(selectedIncident.incident_date).toLocaleDateString('fr-FR')}</p>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase">Unité / Zone</Label>
                  <p className="font-medium">
                    {sites.find(s => s.id === selectedIncident.site_id)?.name || 'N/A'}
                    {selectedIncident.zone_id && ` / ${zones.find(z => z.id === selectedIncident.zone_id)?.name || ''}`}
                  </p>
                </div>

                <div>
                  <Label className="text-xs text-slate-500 uppercase">Description</Label>
                  <p className="text-slate-700">{selectedIncident.description}</p>
                </div>

                {selectedIncident.cause && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Cause</Label>
                    <p className="text-slate-700">{selectedIncident.cause}</p>
                  </div>
                )}

                {selectedIncident.corrective_action && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Action corrective</Label>
                    <p className="text-slate-700">{selectedIncident.corrective_action}</p>
                  </div>
                )}

                {selectedIncident.days_lost > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Jours perdus</Label>
                    <p className="font-medium text-orange-600">{selectedIncident.days_lost} jour(s)</p>
                  </div>
                )}

                {selectedIncident.injured_person && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Personne blessée</Label>
                    <p className="font-medium">{selectedIncident.injured_person}</p>
                  </div>
                )}
              </div>

              {/* Workflow actions */}
              {isManager() && getNextStatus(selectedIncident.status) && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-500 mb-2">Faire avancer le workflow :</p>
                  <Button
                    onClick={() => handleStatusChange(selectedIncident, getNextStatus(selectedIncident.status))}
                    className={getNextStatus(selectedIncident.status) === 'valide' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700'}
                  >
                    {getNextStatus(selectedIncident.status) === 'valide' ? 'Valider l\'incident' : 'Clôturer l\'incident'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Safety;
